import type { TrackWeights, Weather } from "../types";
import type { Dict } from "./i18n";

// Icons are derived from the actual scoring weights/weather value, not from
// the hand-written track label text, so they can never drift out of sync
// with the numbers that drive the race sim.
type Trait = "motor" | "aero" | "pace" | "consistency";

const TRAIT_ICON: Record<Trait, string> = {
  motor: "⚙️",
  aero: "🌬️",
  pace: "🏎️",
  consistency: "🎯",
};

const BALANCED_ICON = "⚖️";

/** Which weight(s) dominate a track's scoring — one or two traits, or "balanced". */
function dominantTraits(weights: TrackWeights): Trait[] | "balanced" {
  const entries: Array<[Trait, number]> = [
    ["motor", weights.teamMotor],
    ["aero", weights.teamAero],
    ["pace", weights.driverPace],
    ["consistency", weights.driverConsistency],
  ];
  const sorted = [...entries].sort((a, b) => b[1] - a[1]);
  const [topKey, topVal] = sorted[0]!;
  const [secondKey, secondVal] = sorted[1]!;
  const [, minVal] = sorted[3]!;
  if (topVal - minVal <= 0.15) return "balanced"; // all four weights are close — no single trait dominates
  if (secondVal >= topVal - 0.08) return [topKey, secondKey]; // two traits dominate together
  return [topKey];
}

/** One or two icons summarizing a track's dominant scoring weight(s). */
export function trackIcon(weights: TrackWeights): string {
  const traits = dominantTraits(weights);
  return traits === "balanced" ? BALANCED_ICON : traits.map((k) => TRAIT_ICON[k]).join("");
}

/** Tooltip text explaining trackIcon()'s glyph for the same weights. */
export function trackIconTitle(weights: TrackWeights, T: Dict): string {
  const traits = dominantTraits(weights);
  return traits === "balanced" ? T.traitTip("balanced") : traits.map((k) => T.traitTip(k)).join(" · ");
}

const WEATHER_ICON: Record<Weather, string> = {
  dry: "☀️",
  light_rain: "🌦️",
  heavy_rain: "⛈️",
};

export function weatherIcon(w: Weather): string {
  return WEATHER_ICON[w];
}

/** Tooltip text explaining weatherIcon()'s glyph for the same weather. */
export function weatherIconTitle(w: Weather, T: Dict): string {
  return T.weatherTip(w);
}

// Inline racing-helmet glyph that marks the player's own driver in race
// results. The shell uses currentColor (so it picks up the row's text colour)
// and the visor is a constant gold band — no good helmet emoji exists.
export const helmetIcon =
  '<svg class="helmet" viewBox="0 0 24 24" aria-hidden="true">' +
  '<path fill="currentColor" d="M12 3C7 3 4 6.5 4 11.5V16c0 1.7 1.3 3 3 3h2l1-2h4l1 2h2c1.7 0 3-1.3 3-3v-4.5C20 6.5 17 3 12 3z"/>' +
  '<rect x="7" y="9" width="10" height="4" rx="2" fill="#d4af37"/>' +
  "</svg>";
